import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyBankPennyless } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import { FiCheckCircle, FiDatabase, FiInfo } from 'react-icons/fi';
import Loader from "../../../Loader";
import { Data3 } from "../Data";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { useService } from "../../../context/ServiceContext";

const BankPennyTest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [bankPennyData, setBankPennyData] = useState({});
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState({});
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [ifscDetails, setIfscDetails] = useState("");
  const { serviceName } = useService();

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Form validation
  const validateForm = (accountNumber, ifsc, ifscDetails) => {
    const errors = {};

    if (!accountNumber.trim()) {
      errors.accountNumber = "Account number is required.";
    } else if (!/^\d{9,18}$/.test(accountNumber)) {
      errors.accountNumber = "Account number must be 9 to 18 digits.";
    }

    if (!ifsc.trim()) {
      errors.ifsc = "IFSC code is required.";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      errors.ifsc = "Invalid IFSC code format.";
    }

    if (ifscDetails === "") {
      errors.ifscDetails = "Please select IFSC details.";
    }

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(accountNumber, ifsc, ifscDetails);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);

      const response = await verifyBankPennyless({
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        account_number: accountNumber,  
        bank_ifsc: ifsc,
        ifsc_details: ifscDetails === "true"
      });

      const statusCode = response?.status?.code || response?.status;

      if (statusCode === 200) {
        const bankData = response?.data;


        // ✅ Check if valid object or array
        if (bankData && (typeof bankData === "object" || Array.isArray(bankData))) {
          setBankPennyData(bankData);
          showSuccessToast(response.status?.message || "Bank account verified successfully.");

          // Clear form
          setAccountNumber("");
          setIfsc("");
          setIfscDetails("");
          setErrors({});
        } else {
        //  console.error("Unexpected bank data format:", bankData);
          showErrorToast("Bank details are missing or in an unexpected format.");
        }
      } else if (statusCode === 422) {
        const backendErrors = response.errors || {};
        if (backendErrors.account_number) showErrorToast(backendErrors.account_number[0]);
        if (backendErrors.bank_ifsc) showErrorToast(backendErrors.bank_ifsc[0]);
        if (backendErrors.ifsc_details) showErrorToast(backendErrors.ifsc_details[0]);

        showErrorToast(response.status?.message || "Invalid bank format or data.");
      }  else if (statusCode === 403) {
        showErrorToast(response.status.message || "Invalid or deactivated API key.");
       }
       else if (statusCode === 500) {
        showErrorToast(response.status?.message || "Server error occurred while fetching bank details.");
      } else {
        showErrorToast(response.status?.message || `Unexpected error occurred (code: ${statusCode})`);
      }
    } catch (error) {
      console.error("Bank Details Error:", error);
      showErrorToast("Failed to verify bank details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 justify-center md:ml-32 ml-0 p-4">
      
      
      <div className="col-span-2 md:col-span-6 flex justify-end">
  <Link
    to="/TestMaster"
    className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-1 py-1 rounded-md font-semibold shadow-md transition duration-300"
  >
    <span>Go Back</span>
    <IoArrowBackCircleOutline size={24} />
  </Link>
</div>

      
      
        <InputWithLabel
          type="text"
          label="Account No"
          placeholder="Enter Account No"
          maxLength="18"
          className="p-1 rounded shadow-lg shadow-blue-500/50 border border-blue-600"
          name="account_number"
          id="account_number"
          required={true}
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          error={errors.accountNumber}
        />

        <InputWithLabel
          type="text"
          label="IFSC"
          placeholder="Enter IFSC"
          maxLength="11"
          className="p-1 rounded shadow-lg shadow-blue-500/50 border border-blue-600"
          name="bank_ifsc"
          id="bank_ifsc"
          required={true}
          value={ifsc}
          onChange={(e) => setIfsc(e.target.value.toUpperCase())}
          error={errors.ifsc}
        />

        <div className="w-full">
          <label htmlFor="ifsc_details" className="block font-serif font-semibold">
            Select IFSC Details<span className="text-red-700 font-bold">*</span>
          </label>
          <select
            className="w-full border border-blue-600 rounded-md py-1 text-center"
            name="ifsc_details"
            id="ifsc_details"
            value={ifscDetails}
            onChange={(e) => setIfscDetails(e.target.value)}
          >
            <option value="" disabled>
              Select IFSC Details
            </option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
          {errors.ifscDetails && <p className="text-red-600 text-sm">{errors.ifscDetails}</p>}
        </div>

        <div>
          <Button
            type="button"
            className="text-center text-white bg-blue-900 shadow-lg bg-gradient-to-r from-blue-400 via-blue-1200 to-blue-900 font-semibold rounded-md px-2 py-1 mt-5 text-md hover:from-blue-900 hover:to-blue-400 border-4 ms-2"
            id="verifyBankDetails"
            name="verifyBankDetails"
            text="Verify"
            onClick={handleSubmit}
          />
        </div>
      </div>

     
      {bankPennyData &&
  typeof bankPennyData === "object" &&
  bankPennyData.full_name &&
  bankPennyData.ifsc_details && (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-4">
      {/* Account Info Card */}
      <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition duration-300">
        <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
          <FiCheckCircle className="text-blue-500 text-2xl" />
          Account Information
        </h2>
        <div className="space-y-2 text-gray-700 text-sm">
          <p><span className="font-bold">Full Name:</span> {bankPennyData?.full_name}</p>
          <p><span className="font-bold">Account Exists:</span> {bankPennyData?.account_exists ? 'Yes' : 'No'}</p>
          <p><span className="font-bold">Client ID:</span> {bankPennyData?.client_id}</p>
        </div>
      </div>

      {/* Bank Details Card */}
      <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition duration-300">
        <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
          <FiDatabase className="text-green-500 text-2xl" />
          Bank Details
        </h2>
        <div className="space-y-2 text-gray-700 text-sm">
          <p><span className="font-bold">Bank Name:</span> {bankPennyData?.ifsc_details?.bank_name}</p>
          <p><span className="font-bold">Branch:</span> {bankPennyData?.ifsc_details?.branch}</p>
          <p><span className="font-bold">IFSC:</span> {bankPennyData?.ifsc_details?.ifsc}</p>
          <p><span className="font-bold">MICR:</span> {bankPennyData?.ifsc_details?.micr}</p>
          <p><span className="font-bold">City:</span> {bankPennyData?.ifsc_details?.city}</p>
          <p><span className="font-bold">State:</span> {bankPennyData?.ifsc_details?.state}</p>
        </div>
      </div>

      {/* Additional Info Card */}
      <div className="sm:col-span-2 bg-gradient-to-br from-yellow-50 to-white border border-yellow-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition duration-300">
        <h2 className="text-xl font-bold text-yellow-700 mb-4 flex items-center gap-2">
          <FiInfo className="text-yellow-500 text-2xl" />
          Additional Info
        </h2>
        <div className="space-y-2 text-gray-700 text-sm">
          <p><span className="font-bold">Address:</span> {bankPennyData?.ifsc_details?.address}</p>
          <p><span className="font-bold">NEFT:</span> {bankPennyData?.ifsc_details?.neft ? 'Available' : 'Not Available'}</p>
          <p><span className="font-bold">RTGS:</span> {bankPennyData?.ifsc_details?.rtgs ? 'Available' : 'Not Available'}</p>
          <p><span className="font-bold">IMPS:</span> {bankPennyData?.ifsc_details?.imps ? 'Available' : 'Not Available'}</p>
          <p><span className="font-bold">UPI:</span> {bankPennyData?.ifsc_details?.upi ? 'Supported' : 'Not Supported'}</p>
        </div>
      </div>

      {/* JSON Preview */}
      <div className="sm:col-span-2 mt-4 p-4 bg-white border border-blue-300 rounded-md shadow-lg">
        <h2 className="text-lg font-semibold text-blue-800 mb-3">Verified Bank Details</h2>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto">
          {JSON.stringify(bankPennyData, null, 2)}
        </pre>
      </div>
    </div>
  )
}




    </>
  );
};

export default BankPennyTest;
